import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users as UsersIcon, Plus, Search, Filter, Download, Upload,
  X, Loader2, Save, ChevronDown, Pencil, CheckSquare, Square,
  ArrowRightLeft, KeyRound, Copy, CheckCircle2, PauseCircle, Trash2, AlertTriangle, Eye, Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { AuthenticatedImage } from '../components/AuthenticatedImage';
import EmployeeViewModal from '../components/EmployeeViewModal';
import EmployeeAutocomplete from '../components/EmployeeAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const PAGE_SIZE = 200;

const Employees = () => {
  const { user, activeCompanyId } = useAuth();
  const [searchParams] = useSearchParams();
  const ctcGroupFromUrl = searchParams.get('ctc_group') || '';
  const [employees, setEmployees] = useState([]);
  const [viewingEmpId, setViewingEmpId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [summaryCounts, setSummaryCounts] = useState({ total: 0, active: 0, notice: 0, newJoiners30d: 0 });
  // Seed the company filter from the currently-active company (set on login / tenant-switch).
  // Empty string = "all companies in tenant". Fallback to '' if not yet resolved.
  const [companyFilter, setCompanyFilter] = useState(() => activeCompanyId || '');
  const [companies, setCompanies] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [empTypes, setEmpTypes] = useState([]);
  // Stage 73.20ci — Departments (merged from db.departments +
  // organizational_units) used by the Department picker.
  const [departments, setDepartments] = useState([]);

  // Add Employee modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addForm, setAddForm] = useState({
    first_name: '', last_name: '', date_of_birth: '', gender: 'male',
    marital_status: 'single', mobile: '', personal_email: '', work_email: '',
    date_of_joining: '', company_id: '', designation_id: '',
    employee_category_id: '', employment_type_id: '',
  });

  // Bulk upload
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleteForm, setBulkDeleteForm] = useState({
    target: 'company',          // company | codes | selected
    company_id: '',
    codes_text: '',
    include_dependents: true,
    hard: false,
    confirm_text: '',
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState('reject'); // reject | update | skip
  const [bulkResult, setBulkResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Edit Employee modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Full Transfer modal
  const [transferEmp, setTransferEmp] = useState(null);
  const [transferForm, setTransferForm] = useState({});
  const [transferring, setTransferring] = useState(false);
  const [transferPreview, setTransferPreview] = useState(null);
  const [structures, setStructures] = useState([]);
  const [ctcGroups, setCtcGroups] = useState([]);

  // Bulk edit / selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    company_id: '', designation_id: '', employee_category_id: '',
    employment_type_id: '', department: '', employment_status: '',
  });
  const [bulkEditing, setBulkEditing] = useState(false);

  // ESS login provisioning
  const [loginPopup, setLoginPopup] = useState(null);          // single-result popup
  const [bulkLoginResult, setBulkLoginResult] = useState(null); // bulk-result modal

  // Send Password to Email
  const [showSendPasswordConfirmModal, setShowSendPasswordConfirmModal] = useState(false);
  const [sendingPasswordEmail, setSendingPasswordEmail] = useState(false);
  const [sendPasswordResult, setSendPasswordResult] = useState(null);

  const handleSendPasswordEmail = async () => {
    if (selectedIds.length === 0) return;
    setSendingPasswordEmail(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/employees/send-password-email`,
        { employee_ids: selectedIds },
        { withCredentials: true }
      );
      setShowSendPasswordConfirmModal(false);
      setSendPasswordResult(data);
      if (data?.summary?.failed === 0) {
        toast.success(`Password emails sent to ${data?.summary?.sent || 0} employee(s)`);
      } else {
        toast.warning(`Sent: ${data?.summary?.sent || 0}, Failed: ${data?.summary?.failed || 0}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send password emails');
    } finally {
      setSendingPasswordEmail(false);
    }
  };

  // Stage 73.19 — CTC source map: { [employee_id]: { structure_source, ctc_group_id } }.
  // Populated whenever a company is selected so the table can render a Group vs
  // Custom badge per row without N round-trips.
  const [ctcSourceMap, setCtcSourceMap] = useState({});

  const fetchEmployees = useCallback(async ({ append = false } = {}) => {
    try {
      setLoadError(null);
      if (append) setLoadingMore(true); else setLoading(true);
      const targetPage = append ? pageRef.current + 1 : 1;
      const params = { page: targetPage, limit: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (companyFilter) params.company_id = companyFilter;
      if (searchTerm) params.search = searchTerm;

      const { data } = await axios.get(`${API_URL}/employees/page`, { params, withCredentials: true });
      const items = data.employees || [];
      const total = data.total || 0;

      if (data.counts) {
        setSummaryCounts({
          total: data.counts.total ?? 0,
          active: data.counts.active ?? 0,
          notice: data.counts.notice ?? 0,
          newJoiners30d: data.counts.newJoiners30d ?? 0,
        });
      }

      if (append) {
        setEmployees(prev => {
          const existingIds = new Set(prev.map(emp => emp.id));
          const newItems = items.filter(emp => !existingIds.has(emp.id));
          return [...prev, ...newItems];
        });
        pageRef.current = targetPage;
      } else {
        setEmployees(items);
        pageRef.current = 1;
      }
      setTotalEmployees(total);
      setHasMore(!!data.hasMore);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      setLoadError('Unable to load more employees. Please try again.');
      toast.error('Unable to load more employees. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [companyFilter, statusFilter, searchTerm]);
  const fetchMasters = useCallback(async () => {
    try {
      const [compRes, desRes, catRes, typeRes, deptRes] = await Promise.all([
        axios.get(`${API_URL}/companies`, { withCredentials: true }),
        axios.get(`${API_URL}/masters/designations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/masters/employee-categories`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/masters/employment-types`, { withCredentials: true }).catch(() => ({ data: [] })),
        // Stage 73.20ci — merged departments endpoint.
        axios.get(`${API_URL}/employees/_masters/departments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCompanies(Array.isArray(compRes.data) ? compRes.data : []);
      setDesignations(Array.isArray(desRes.data) ? desRes.data : []);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      setEmpTypes(Array.isArray(typeRes.data) ? typeRes.data : []);
      setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
      const preferred = companyFilter || activeCompanyId || compRes.data?.[0]?.id;
      if (preferred && (!addForm.company_id || addForm.company_id !== preferred)) {
        setAddForm(f => ({ ...f, company_id: preferred }));
      }
    } catch (err) {
      console.error('Failed to fetch masters:', err);
    }
  }, [activeCompanyId, addForm, companyFilter]);

  // Fetch page 1 when filters or search change
  useEffect(() => {
    setSelectedIds([]);
    fetchEmployees({ append: false });
    fetchMasters();
  }, [statusFilter, companyFilter, searchTerm]);

  // Refresh the CTC source map whenever the company filter resolves to a
  // single company. (Skipped for the "all companies" / cross-tenant view.)
  useEffect(() => {
    if (!companyFilter) { setCtcSourceMap({}); return; }
    let cancelled = false;
    axios.get(`${API_URL}/payroll/employee-ctc/source-map`, {
      params: { company_id: companyFilter }, withCredentials: true,
    }).then(r => { if (!cancelled) setCtcSourceMap(r.data || {}); })
      .catch(() => { if (!cancelled) setCtcSourceMap({}); });
    return () => { cancelled = true; };
  }, [companyFilter]);

  // Seed the company filter from the globally-active company whenever it changes (e.g. user switches company in TopBar).
  useEffect(() => {
    if (activeCompanyId) setCompanyFilter(activeCompanyId);
  }, [activeCompanyId]);

  const handleLoadMore = () => fetchEmployees({ append: true });




  const getStatusColor = (status) => {
    const st = (status || '').toLowerCase();
    const colors = {
      active: 'bg-green-50 text-green-700 border-green-200',
      notice: 'bg-amber-50 text-amber-700 border-amber-200',
      on_notice: 'bg-amber-50 text-amber-700 border-amber-200',
      notice_period: 'bg-amber-50 text-amber-700 border-amber-200',
      resigned: 'bg-amber-50 text-amber-700 border-amber-200',
      terminated: 'bg-red-50 text-red-700 border-red-200',
    };
    return colors[st] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getCompletenessColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  // ======= EDIT EMPLOYEE =======
  const openEditModal = (emp) => {
    setEditingEmp(emp);
    setEditForm({
      first_name: emp.personal_details?.first_name || '',
      last_name: emp.personal_details?.last_name || '',
      date_of_birth: emp.personal_details?.date_of_birth || '',
      gender: emp.personal_details?.gender || 'male',
      marital_status: emp.personal_details?.marital_status || 'single',
      mobile: emp.personal_details?.mobile || '',
      personal_email: emp.personal_details?.personal_email || '',
      work_email: emp.employment_details?.work_email || '',
      date_of_joining: emp.employment_details?.date_of_joining || '',
      company_id: emp.employment_details?.company_id || '',
      designation_id: emp.employment_details?.designation_id || '',
      employee_category_id: emp.employment_details?.employee_category_id || '',
      employment_type_id: emp.employment_details?.employment_type_id || '',
      department: emp.employment_details?.department || '',
      // Stage 73.20ci — proper Master-IDs for Department + Reporting Manager.
      department_id: emp.employment_details?.department_id || '',
      reporting_manager_id: emp.employment_details?.reporting_manager_id || '',
      reporting_manager_name: emp.employment_details?.reporting_manager_name || '',
      employment_status: emp.employment_details?.employment_status || 'active',
      // O&M / per-day-wage flow
      skill_category: emp.employment_details?.skill_category || '',
      daily_wage_rate_override: emp.employment_details?.daily_wage_rate_override || '',
      location: emp.employment_details?.location || '',
      // === KYC + Statutory codes ===
      // Identifiers (employee's personal numbers) + Establishment codes (drive
      // PF / ESI challan grouping at run time).
      pan_number: emp.kyc_statutory_details?.pan_number || '',
      aadhaar_number: emp.kyc_statutory_details?.aadhaar_number || '',
      uan_number: emp.kyc_statutory_details?.uan_number || '',
      esi_number: emp.kyc_statutory_details?.esi_number || '',
      pf_code: emp.kyc_statutory_details?.pf_code || '',
      esi_code: emp.kyc_statutory_details?.esi_code || '',
    });
    setShowEditModal(true);
  };

  const handleEditEmployee = async () => {
    if (!editingEmp) return;
    if (!editForm.first_name || !editForm.last_name || !editForm.work_email) {
      toast.error('First name, last name, and work email are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        personal_details: {
          first_name: editForm.first_name,
          last_name: editForm.last_name,
          date_of_birth: editForm.date_of_birth,
          gender: editForm.gender,
          marital_status: editForm.marital_status,
          mobile: editForm.mobile,
          personal_email: editForm.personal_email || null,
        },
        employment_details: {
          employee_code: editingEmp.employment_details?.employee_code || '',
          company_id: editForm.company_id,
          designation_id: editForm.designation_id || null,
          employee_category_id: editForm.employee_category_id || null,
          employment_type_id: editForm.employment_type_id || null,
          date_of_joining: editForm.date_of_joining,
          work_email: editForm.work_email,
          department: editForm.department || null,
          // Stage 73.20ci — Persist the resolved Master IDs.
          department_id: editForm.department_id || null,
          reporting_manager_id: editForm.reporting_manager_id || null,
          reporting_manager_name: editForm.reporting_manager_name || null,
          employment_status: editForm.employment_status,
          skill_category: (editForm.skill_category || '').trim() || null,
          daily_wage_rate_override: editForm.daily_wage_rate_override
            ? Number(editForm.daily_wage_rate_override) : null,
          location: (editForm.location || '').trim() || null,
        },
        kyc_statutory_details: {
          pan_number: (editForm.pan_number || '').trim() || null,
          aadhaar_number: (editForm.aadhaar_number || '').trim() || null,
          uan_number: (editForm.uan_number || '').trim() || null,
          esi_number: (editForm.esi_number || '').trim() || null,
          // PF / ESI establishment codes — these power the monthly challans.
          pf_code: (editForm.pf_code || '').trim() || null,
          esi_code: (editForm.esi_code || '').trim() || null,
        },
      };
      await axios.patch(`${API_URL}/employees/${editingEmp.id}`, payload, { withCredentials: true });
      toast.success('Employee updated');
      setShowEditModal(false);
      setEditingEmp(null);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  // ======= FULL TRANSFER =======
  const openTransferModal = async (emp) => {
    setTransferEmp(emp);
    setTransferForm({
      effective_date: new Date().toISOString().slice(0, 10),
      new_ctc_group_id: '',
      new_structure_code: '',
      new_ctc_annual: '',
      new_department_id: emp.employment_details?.department_id || '',
      new_location_id: emp.employment_details?.location_id || '',
      new_designation: emp.employment_details?.designation || '',
      new_grade: emp.employment_details?.grade || '',
      remarks: '',
    });
    setTransferPreview(null);
    // Lazy-load structures + CTC groups for the picker on first open.
    const cid = emp.employment_details?.company_id;
    if (cid) {
      try {
        const [sRes, gRes] = await Promise.all([
          axios.get(`${API_URL}/payroll/structures?company_id=${cid}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/controls/ctc-groups?company_id=${cid}`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setStructures(sRes.data || []);
        setCtcGroups(gRes.data || []);
      } catch (e) { /* non-fatal */ }
    }
  };

  // Live statutory eligibility preview on the transfer modal — updates whenever the user
  // changes the new CTC, so they can see PF/ESI/PT impact BEFORE saving.
  useEffect(() => {
    if (!transferEmp || !transferForm.new_ctc_annual) { setTransferPreview(null); return; }
    const monthly = (parseFloat(transferForm.new_ctc_annual) || 0) / 12;
    setTransferPreview({
      monthly_ctc: Math.round(monthly),
      pf_applicable: monthly <= 15000,
      esi_applicable: monthly > 0 && monthly <= 21000,
      pt_applicable: monthly > 0,
    });
  }, [transferEmp, transferForm.new_ctc_annual]);

  const submitTransfer = async () => {
    if (!transferEmp) return;
    if (!transferForm.effective_date) { toast.error('Effective date is required'); return; }
    setTransferring(true);
    try {
      const payload = { ...transferForm };
      // Coerce empty strings to null so backend does not overwrite with blanks.
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === undefined) delete payload[k]; });
      if (payload.new_ctc_annual) payload.new_ctc_annual = parseFloat(payload.new_ctc_annual);
      const { data } = await axios.post(`${API_URL}/employees/${transferEmp.id}/full-transfer`, payload, { withCredentials: true });
      const e = data.statutory_eligibility || {};
      toast.success(
        `Transferred. Structure: ${data.applied_structure || 'unchanged'} · Monthly: ₹${(data.new_ctc_monthly || 0).toLocaleString('en-IN')} · PF:${e.pf_applicable ? '✓' : '✗'} ESI:${e.esi_applicable ? '✓' : '✗'}`,
      );
      setTransferEmp(null);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally { setTransferring(false); }
  };

  // ======= SELECTION HELPERS =======
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleSelectAll = () => {
    const visibleIds = employees.map((e) => e.id);
    const allSelected = visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  };
  const isAllSelected =
    employees.length > 0 &&
    employees.every((e) => selectedIds.includes(e.id));

  // ======= BULK EDIT =======
  const handleBulkEdit = async () => {
    if (selectedIds.length === 0) { toast.error('No employees selected'); return; }
    const cleanUpdates = Object.fromEntries(
      Object.entries(bulkEditForm).filter(([, v]) => v !== '' && v != null)
    );
    if (Object.keys(cleanUpdates).length === 0) {
      toast.error('Select at least one field to update'); return;
    }
    setBulkEditing(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/employees/bulk-edit`,
        { employee_ids: selectedIds, updates: cleanUpdates },
        { withCredentials: true }
      );
      toast.success(data.message || 'Bulk update applied');
      setShowBulkEditModal(false);
      setSelectedIds([]);
      setBulkEditForm({
        company_id: '', designation_id: '', employee_category_id: '',
        employment_type_id: '', department: '', employment_status: '',
      });
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk edit failed');
    } finally {
      setBulkEditing(false);
    }
  };

  // ======= ADD EMPLOYEE =======
  const handleAddEmployee = async () => {
    if (!addForm.first_name || !addForm.last_name || !addForm.date_of_birth || !addForm.date_of_joining || !addForm.work_email || !addForm.mobile) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tenant_id: user?.tenant_id || '',
        personal_details: {
          first_name: addForm.first_name,
          last_name: addForm.last_name,
          date_of_birth: addForm.date_of_birth,
          gender: addForm.gender,
          marital_status: addForm.marital_status,
          mobile: addForm.mobile,
          personal_email: addForm.personal_email || null,
        },
        employment_details: {
          employee_code: '',
          company_id: addForm.company_id,
          designation_id: addForm.designation_id || designations[0]?.id || 'default',
          employee_category_id: addForm.employee_category_id || categories[0]?.id || 'default',
          employment_type_id: addForm.employment_type_id || empTypes[0]?.id || 'default',
          date_of_joining: addForm.date_of_joining,
          work_email: addForm.work_email,
        },
      };
      const { data: created } = await axios.post(`${API_URL}/employees`, payload, { withCredentials: true });
      toast.success('Employee added successfully');
      // If backend auto-provisioned an ESS login, surface the credentials once.
      if (created?.ess_login && created.ess_login.status === 'created' && created.ess_login.password) {
        setLoginPopup({
          mode: 'create',
          name: created.ess_login.name || `${addForm.first_name} ${addForm.last_name}`,
          email: created.ess_login.email,
          password: created.ess_login.password,
        });
      } else if (created?.ess_login?.status === 'skipped_no_email') {
        toast.warning('No work email — ESS login skipped. Add an email and use the "Login" button to generate one.');
      }
      setShowAddModal(false);
      setAddForm({
        first_name: '', last_name: '', date_of_birth: '', gender: 'male',
        marital_status: 'single', mobile: '', personal_email: '', work_email: '',
        date_of_joining: '', company_id: companies[0]?.id || '', designation_id: '',
        employee_category_id: '', employment_type_id: '',
      });
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteForm.confirm_text || bulkDeleteForm.confirm_text.toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE in capitals to confirm');
      return;
    }
    const body = {
      include_dependents: bulkDeleteForm.include_dependents,
      hard: bulkDeleteForm.hard,
    };
    if (bulkDeleteForm.target === 'company') {
      if (!bulkDeleteForm.company_id) { toast.error('Pick a company'); return; }
      body.company_id = bulkDeleteForm.company_id;
    } else if (bulkDeleteForm.target === 'codes') {
      const codes = bulkDeleteForm.codes_text.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      if (codes.length === 0) { toast.error('Enter at least one employee code'); return; }
      body.employee_codes = codes;
    } else if (bulkDeleteForm.target === 'selected') {
      if (!selectedIds || selectedIds.length === 0) { toast.error('No employees selected'); return; }
      body.employee_ids = selectedIds;
    }
    setBulkDeleting(true);
    setBulkDeleteResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/employees/bulk-delete`, body, { withCredentials: true });
      setBulkDeleteResult(data);
      const verb = data.mode === 'hard' ? 'permanently deleted' : 'deactivated';
      toast.success(`${data.deleted_employees} employee(s) ${verb}`);
      // Reset selection + refresh table.
      if (typeof setSelectedIds === 'function') setSelectedIds([]);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ======= BULK UPLOAD =======
  // `modeOverride` is optional and ONLY accepts the 3 valid strings — used by
  // the Stage 73.5 retry buttons. When `onClick={handleBulkUpload}` is used
  // directly on the main Upload button, React passes its SyntheticEvent as the
  // first arg; guard against that by ignoring anything that isn't a string of
  // the expected 3 values. Without this, the URL parameter becomes
  // `duplicate_mode=[object Object]` and the backend correctly rejects with
  // 400 "duplicate_mode must be reject | update | skip".
  const handleBulkUpload = async (modeOverride) => {
    if (!bulkFile) { toast.error('Select a CSV file'); return; }
    const validOverride = ['reject', 'update', 'skip'].includes(modeOverride) ? modeOverride : null;
    setUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const compId = addForm.company_id || companies[0]?.id || '';
      const params = new URLSearchParams({
        company_id: compId,
        duplicate_mode: validOverride || duplicateMode,
        dry_run: 'false',
      });
      const { data } = await axios.post(`${API_URL}/employees/bulk-upload?${params}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(data);
      if (data.rejected_whole_file) {
        // Differentiate between "duplicates" (recoverable via mode switch) and
        // intra-file duplicates / data errors (need CSV edit).
        const allDupes = (data.errors || []).every(e => (e.error || '').includes('already exists'));
        if (allDupes && data.errors?.length > 0) {
          toast.error(`${data.errors.length} rows already exist — pick Update or Skip mode below to retry.`);
        } else {
          toast.error('File rejected — see details below');
        }
      } else if (data.success_count > 0) {
        const summary = `${data.created_count || 0} created, ${data.updated_count || 0} updated, ${data.skipped_count || 0} skipped`;
        toast.success(summary);
        fetchEmployees();
      }
      if (data.error_count > 0 && !data.rejected_whole_file) toast.error(`${data.error_count} rows had errors`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const compId = companyFilter || addForm.company_id || companies[0]?.id || '';
      const response = await axios.get(`${API_URL}/employees/bulk-upload/template${compId ? '?company_id=' + compId : ''}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_bulk_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  // ======= EXPORT =======
  const handleExport = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (companyFilter) params.company_id = companyFilter;
      const response = await axios.get(`${API_URL}/employees/export/csv`, {
        params,
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="employees-page">
        {/* Banner when navigated from a CTC Group */}
        {ctcGroupFromUrl && (
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 flex items-center gap-3" data-testid="ctc-group-banner">
            <div className="text-2xl">🎯</div>
            <div className="flex-1">
              <div className="font-semibold text-blue-900">Assign employees to this CTC Group</div>
              <div className="text-xs text-blue-800 mt-0.5">
                Tick the employees you want to assign, then use <b>Bulk Transfer → CTC Group</b> (top right) to map them.
                Each employee keeps their own CTC amount — only the structure template changes.
              </div>
            </div>
            <a href="/business-controls?tab=ctc" className="text-xs text-blue-600 hover:underline">← Back to CTC Groups</a>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <UsersIcon className="inline-block w-8 h-8 mr-2 mb-1" />
              Employees
            </h1>
            <p className="mt-1 text-sm text-slate-600">Manage employee lifecycle - onboarding to exit</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" data-testid="bulk-upload-button" onClick={() => setShowBulkModal(true)}>
              <Upload className="w-4 h-4 mr-2" /> Bulk Upload
            </Button>
            <Button variant="outline" data-testid="bulk-delete-button" onClick={() => setShowBulkDelete(true)} className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
              <Trash2 className="w-4 h-4 mr-2" /> Bulk Delete
            </Button>
            <Button variant="outline" data-testid="export-button" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button data-testid="add-employee-button" onClick={() => setShowAddModal(true)}
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Employee
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3" data-testid="employees-filters">
          {companies.length > 1 && (
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              data-testid="company-filter-select"
              className="h-10 px-3 bg-white border-2 border-[#0052FF] rounded-md text-sm font-semibold text-[#0052FF] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0052FF]/30"
            >
              <option value="">All companies in tenant</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input type="text" placeholder="Search by name, code, or email..." data-testid="search-employees-input"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#0052FF]" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="status-filter-select"
            className="h-10 px-4 bg-white border border-slate-300 rounded-md text-sm cursor-pointer">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="notice">Notice</option>
            <option value="resigned">Resigned</option>
            <option value="terminated">Terminated</option>
          </select>
          {companyFilter && companies.length > 1 && (
            <div className="ml-auto text-xs text-slate-600" data-testid="employees-scope-hint">
              Showing <strong className="text-[#0052FF]">{companies.find(c => c.id === companyFilter)?.name || '—'}</strong> · <button onClick={() => setCompanyFilter('')} className="text-[#0052FF] hover:underline" data-testid="employees-scope-clear">View all</button>
            </div>
          )}
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.length > 0 && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="bulk-action-bar">
            <p className="text-sm text-blue-900">
              <strong>{selectedIds.length}</strong> employee{selectedIds.length > 1 ? 's' : ''} selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid="bulk-edit-btn"
                onClick={() => setShowBulkEditModal(true)}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Bulk Edit
              </Button>
              <Button variant="outline" size="sm" data-testid="bulk-create-logins-btn"
                onClick={async () => {
                  const force = window.confirm(`Generate ESS logins for ${selectedIds.length} employee(s)?\n\nOK = FORCE-RESET passwords (returns CSV with all credentials).\nCancel = LINK existing accounts only (no password reset).`);
                  try {
                    const { data } = await axios.post(
                      `${API_URL}/employees/bulk-create-logins`,
                      { employee_ids: selectedIds, force_reset: force },
                      { withCredentials: true }
                    );
                    setBulkLoginResult(data);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Bulk login failed');
                  }
                }}>
                <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Generate Logins
              </Button>
              <Button variant="outline" size="sm" data-testid="send-password-email-btn"
                className="bg-[#0052FF] text-white hover:bg-[#0043D1] border-[#0052FF]"
                onClick={() => setShowSendPasswordConfirmModal(true)}>
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Send Password to Email
              </Button>
              <Button variant="outline" size="sm" data-testid="bulk-clear-selection"
                onClick={() => setSelectedIds([])}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div 
            onClick={() => setStatusFilter('all')}
            className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
              statusFilter === 'all' ? 'border-[#0052FF] ring-2 ring-[#0052FF]/20' : 'border-slate-200'
            }`}
            data-testid="total-employees-card"
            title="Click to view all employees"
          >
            <p className="text-xs font-semibold uppercase text-slate-500">Total Employees</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono" data-testid="total-employees-count">{summaryCounts.total}</p>
          </div>
          <div 
            onClick={() => setStatusFilter('active')}
            className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
              statusFilter === 'active' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-slate-200'
            }`}
            data-testid="active-employees-card"
            title="Click to filter active employees"
          >
            <p className="text-xs font-semibold uppercase text-slate-500">Active</p>
            <p className="text-2xl font-semibold text-green-600 mt-1 font-mono" data-testid="active-employees-count">
              {summaryCounts.active}
            </p>
          </div>
          <div 
            onClick={() => setStatusFilter(statusFilter === 'notice' ? 'all' : 'notice')}
            className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
              statusFilter === 'notice' ? 'border-amber-500 ring-2 ring-amber-500/20' : 'border-slate-200'
            }`}
            data-testid="on-notice-card"
            title="Click to filter employees on notice"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase text-slate-500">On Notice</p>
              {statusFilter === 'notice' && (
                <span className="text-[10px] bg-amber-100 text-amber-800 font-semibold px-1.5 py-0.5 rounded">Filtered</span>
              )}
            </div>
            <p className="text-2xl font-semibold text-amber-600 mt-1 font-mono" data-testid="on-notice-count">
              {summaryCounts.notice}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="new-joiners-card">
            <p className="text-xs font-semibold uppercase text-slate-500">New Joiners (30d)</p>
            <p className="text-2xl font-semibold text-blue-600 mt-1 font-mono" data-testid="new-joiners-count">
              {summaryCounts.newJoiners30d}
            </p>
          </div>
        </div>

        {/* Employee Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm flex flex-col">
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] min-h-[400px] relative">
            <table className="w-full min-w-[1850px] border-separate border-spacing-0 whitespace-nowrap" data-testid="employees-table">
              <thead className="bg-slate-50 sticky top-0 z-30 border-b border-slate-200">
                <tr>
                  <th className="sticky left-0 top-0 z-40 bg-slate-50 py-3 px-4 w-[48px] min-w-[48px] max-w-[48px] border-b border-slate-200">
                    <button onClick={toggleSelectAll} data-testid="select-all-employees" className="text-slate-500 hover:text-slate-700">
                      {isAllSelected ? <CheckSquare className="w-4 h-4 text-[#0052FF]" /> : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="sticky left-[48px] top-0 z-40 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[140px] border-b border-slate-200">Employee Code</th>
                  <th className="sticky left-[188px] top-0 z-40 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[220px] border-b border-slate-200 border-r border-slate-200 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)]">Name</th>
                  <th className="sticky top-0 z-20 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[200px] border-b border-slate-200">Email</th>
                  <th className="sticky top-0 z-20 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[150px] border-b border-slate-200">Department</th>
                  <th className="sticky top-0 z-20 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[160px] border-b border-slate-200">Designation</th>
                  <th className="sticky top-0 z-20 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[140px] border-b border-slate-200" title="Stage 73.19 — which CTC bifurcation pays this employee. Group = standard template. Custom = uploaded per-employee bifurcation.">CTC Source</th>
                  <th className="sticky top-0 z-20 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[120px] border-b border-slate-200">Status</th>
                  <th className="sticky top-0 z-20 bg-slate-50 text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[160px] border-b border-slate-200">Completeness</th>
                  <th className="sticky top-0 z-20 bg-slate-50 text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase min-w-[550px] border-b border-slate-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
                ) : employees.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-12 text-slate-500">
                    <UsersIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No employees found</p>
                    <p className="text-xs mt-1">Start by adding your first employee</p>
                  </td></tr>
                ) : (
                  employees.map((emp) => {
                    const isSelected = selectedIds.includes(emp.id);
                    const stickyBg = isSelected ? 'bg-blue-50 group-hover:bg-blue-100/70' : 'bg-white group-hover:bg-slate-50';
                    const deptDisplay = emp.employment_details?.department_name || emp.employment_details?.department || departments.find(d => d.id === emp.employment_details?.department_id)?.name || '—';
                    const desigDisplay = emp.employment_details?.designation_name || emp.employment_details?.designation || designations.find(d => d.id === emp.employment_details?.designation_id)?.name || '—';

                    return (
                      <tr key={emp.id} data-testid={`employee-row-${emp.employment_details?.employee_code}`}
                        className={`group border-b border-slate-100 hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50/40' : ''}`}>
                        <td className={`sticky left-0 z-20 ${stickyBg} py-3 px-4 w-[48px] min-w-[48px] max-w-[48px] transition-colors border-b border-slate-100`}>
                          <button onClick={() => toggleSelect(emp.id)} data-testid={`select-emp-${emp.id}`} className="text-slate-500 hover:text-slate-700">
                            {isSelected ? <CheckSquare className="w-4 h-4 text-[#0052FF]" /> : <Square className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className={`sticky left-[48px] z-20 ${stickyBg} py-3 px-4 text-sm text-slate-900 font-mono font-medium min-w-[140px] transition-colors border-b border-slate-100`}>
                          {emp.employment_details?.employee_code || 'N/A'}
                        </td>
                        <td className={`sticky left-[188px] z-20 ${stickyBg} py-3 px-4 text-sm text-slate-900 font-medium min-w-[220px] border-r border-slate-200 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)] transition-colors border-b border-slate-100`}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8 flex items-center justify-center overflow-hidden shrink-0">
                              <AuthenticatedImage 
                                src={emp.personal_details?.photoUrl || emp.personal_details?.photo_url || emp.photoUrl} 
                                alt={`${emp.personal_details?.first_name} ${emp.personal_details?.last_name}`} 
                                className="h-full w-full object-cover rounded-full" 
                                fallbackText={((emp.personal_details?.first_name?.[0] || '') + (emp.personal_details?.last_name?.[0] || '')).toUpperCase() || 'EMP'}
                              />
                            </Avatar>
                            <span className="truncate">{emp.personal_details?.first_name} {emp.personal_details?.last_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700 border-b border-slate-100">
                          {emp.employment_details?.work_email || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700 border-b border-slate-100">
                          {deptDisplay}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700 border-b border-slate-100">
                          {desigDisplay}
                        </td>
                        <td className="py-3 px-4 text-sm border-b border-slate-100" data-testid={`ctc-source-${emp.employment_details?.employee_code}`}>
                          {(() => {
                            const src = ctcSourceMap[emp.id]?.structure_source;
                            if (src === 'employee_custom') {
                              return (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-800 border border-violet-200"
                                  title="Paid from an employee-specific custom bifurcation (Bulk Upload page). CTC Group template is bypassed."
                                >
                                  CUSTOM
                                </span>
                              );
                            }
                            if (src === 'group') {
                              const code = emp.employment_details?.ctc_group_code;
                              return (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200"
                                  title={`Paid from CTC Group template${code ? ` "${code}"` : ''}`}
                                >
                                  GROUP{code ? ` · ${code}` : ''}
                                </span>
                              );
                            }
                            return <span className="text-[10px] text-slate-400">—</span>;
                          })()}
                        </td>
                        <td className="py-3 px-4 text-sm border-b border-slate-100">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(emp.employment_details?.employment_status)}`}>
                            {emp.employment_details?.employment_status || 'unknown'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm border-b border-slate-100">
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-slate-200 rounded-full h-2 max-w-24">
                              <div className="bg-[#0052FF] h-2 rounded-full" style={{ width: `${emp.profile_completeness || 0}%` }}></div>
                            </div>
                            <span className={`text-xs font-medium ${getCompletenessColor(emp.profile_completeness)}`}>
                              {emp.profile_completeness || 0}%
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right border-b border-slate-100">
                          <div className="flex items-center justify-end gap-1.5">
                            <button data-testid={`view-emp-${emp.id}`} onClick={() => setViewingEmpId(emp.id)}
                              className="px-2 py-1 bg-[#0052FF]/10 hover:bg-[#0052FF]/20 text-[#0052FF] rounded text-xs font-medium border border-[#0052FF]/30 flex items-center gap-1"
                              title="View 360° Employee Details, CTC Breakup & Generate Appointment Letter">
                              <Eye className="w-3 h-3" /> View
                            </button>
                            <button data-testid={`edit-emp-${emp.id}`} onClick={() => openEditModal(emp)}
                              className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-medium border border-blue-200 flex items-center gap-1">
                              <Pencil className="w-3 h-3" /> Edit
                            </button>
                            <Link
                              to={`/employees/${emp.id}/assets-docs`}
                              data-testid={`assets-docs-emp-${emp.id}`}
                              title="Assets & Documents"
                              className="px-2 py-1 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded text-xs font-medium border border-violet-200 flex items-center gap-1"
                            >
                              📎 Assets
                            </Link>
                            <button data-testid={`login-emp-${emp.id}`} onClick={async () => {
                              const force = window.confirm(`Generate ESS login for ${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}?\n\nClick OK to FORCE-RESET the password (will be shown once),\nor Cancel if you only want to LINK / view status.`);
                              try {
                                const url = `${API_URL}/employees/${emp.id}/create-login${force ? '?force_reset=true' : ''}`;
                                const { data } = await axios.post(url, {}, { withCredentials: true });
                                if (data.password) {
                                  setLoginPopup({ mode: data.status, name: data.name, email: data.email, password: data.password });
                                } else if (data.status === 'exists') {
                                  toast.success(`Login already exists: ${data.email}`);
                                } else {
                                  toast.success(`Status: ${data.status}`);
                                }
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to provision login');
                              }
                            }}
                              className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded text-xs font-medium border border-emerald-200 flex items-center gap-1"
                              title="Create or reset the ESS portal login for this employee">
                              <KeyRound className="w-3 h-3" /> Login
                            </button>
                            <button data-testid={`transfer-emp-${emp.id}`} onClick={() => openTransferModal(emp)}
                              className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-xs font-medium border border-purple-200 flex items-center gap-1"
                              title="Move to a different CTC group / structure / designation / location with statutory recompute">
                              <ArrowRightLeft className="w-3 h-3" /> Transfer
                            </button>
                            {emp.employment_details?.employment_status === 'active' && (
                              <button data-testid={`hold-emp-${emp.id}`} onClick={async () => {
                                const choice = window.prompt(
                                  `Create a HR Bench HOLD event for ${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}.\n\nType the hold level:\n  1 = Payroll Hold  (skip this employee from the next run entirely)\n  2 = Payment Hold  (process payroll but block payout)\n  0 = Active       (clear any existing hold)\n\nEnter 0, 1 or 2:`,
                                  '2',
                                );
                                if (!choice) return;
                                const mapping = { '0': 'active', '1': 'payroll_hold', '2': 'payment_hold' };
                                const payroll_status = mapping[choice.trim()];
                                if (!payroll_status) { toast.error('Invalid choice'); return; }
                                const reason = window.prompt('Reason for this change (optional):', '') || '';
                                try {
                                  await axios.post(`${API_URL}/hr/lifecycle/events`, {
                                    event_type: 'hold',
                                    employee_id: emp.id,
                                    reason,
                                    payload: { payroll_status, hold_reason: reason },
                                  }, { withCredentials: true });
                                  toast.success(`Hold event created. An HR admin must approve it in HR Workbench → Lifecycle Events.`);
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create hold event'); }
                              }} className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded text-xs font-medium border border-amber-200 flex items-center gap-1"
                                title="Create a pending HR Bench Hold event. An HR admin approves it to flip payroll status.">
                                <PauseCircle className="w-3 h-3" /> Hold
                              </button>
                            )}
                            {emp.employment_details?.employment_status === 'active' && (
                              <button data-testid={`deactivate-emp-${emp.id}`} onClick={async () => {
                                if (!window.confirm(`Deactivate ${emp.personal_details?.first_name} ${emp.personal_details?.last_name}? They can be reactivated later.`)) return;
                                try {
                                  await axios.delete(`${API_URL}/employees/${emp.id}`, { withCredentials: true });
                                  toast.success('Employee deactivated');
                                  fetchEmployees();
                                } catch (e) { toast.error('Failed to deactivate'); }
                              }} className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded text-xs font-medium border border-amber-200">
                                Deactivate
                              </button>
                            )}
                            <button data-testid={`delete-emp-${emp.id}`} onClick={async () => {
                              const name = `${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}`.trim();
                              if (!window.confirm(`Permanently DELETE ${name}? This cannot be undone.`)) return;
                              try {
                                await axios.delete(`${API_URL}/employees/${emp.id}?hard=true`, { withCredentials: true });
                                toast.success('Employee deleted');
                                fetchEmployees();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                            }} className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-medium border border-red-200">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Load-more / pagination footer — surfaces only when the cursor
              endpoint reports `has_more`. Lets the page scale to 50K+ employees
              without ever hitting the legacy /employees 1000-row cap. */}
          {((hasMore || employees.length > 0) || loadError) && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2 bg-slate-50 text-xs"
                 data-testid="employees-load-more-bar">
              <div className="text-slate-600 flex items-center gap-2">
                <span>
                  Loaded <span className="font-mono font-semibold text-slate-900">{employees.length.toLocaleString('en-IN')}</span> of <span className="font-mono font-semibold text-slate-900">{totalEmployees.toLocaleString('en-IN')}</span> employees
                </span>
                {loadError && <span className="text-red-600 font-medium" data-testid="load-more-error">{loadError}</span>}
              </div>
              {hasMore ? (
                <button onClick={handleLoadMore} disabled={loading || loadingMore}
                  data-testid="employees-load-more-btn"
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                  {loadingMore ? 'Loading...' : `Load next ${PAGE_SIZE}`}
                </button>
              ) : (
                employees.length > 0 && <span className="text-slate-500 font-medium">All employees loaded</span>
              )}
            </div>
          )}
        </div>

        {/* ======= ADD EMPLOYEE MODAL ======= */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-employee-modal">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Add New Employee</h2>
                <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600" data-testid="close-add-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">First Name *</label>
                    <input data-testid="emp-first-name" value={addForm.first_name} onChange={e => setAddForm({...addForm, first_name: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="First name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Last Name *</label>
                    <input data-testid="emp-last-name" value={addForm.last_name} onChange={e => setAddForm({...addForm, last_name: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Last name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Date of Birth *</label>
                    <input data-testid="emp-dob" type="date" value={addForm.date_of_birth} onChange={e => setAddForm({...addForm, date_of_birth: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Gender</label>
                    <select data-testid="emp-gender" value={addForm.gender} onChange={e => setAddForm({...addForm, gender: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Mobile *</label>
                    <input data-testid="emp-mobile" value={addForm.mobile} onChange={e => setAddForm({...addForm, mobile: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="10-digit mobile" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Work Email *</label>
                    <input data-testid="emp-work-email" type="email" value={addForm.work_email} onChange={e => setAddForm({...addForm, work_email: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="work@company.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Date of Joining *</label>
                    <input data-testid="emp-doj" type="date" value={addForm.date_of_joining} onChange={e => setAddForm({...addForm, date_of_joining: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Company *</label>
                    <select data-testid="emp-company" value={addForm.company_id} onChange={e => setAddForm({...addForm, company_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Designation</label>
                    <select data-testid="emp-designation" value={addForm.designation_id} onChange={e => setAddForm({...addForm, designation_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">Select...</option>
                      {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                    <select data-testid="emp-category" value={addForm.employee_category_id} onChange={e => setAddForm({...addForm, employee_category_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">Select...</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
                <Button variant="outline" onClick={() => setShowAddModal(false)} data-testid="cancel-add-employee">Cancel</Button>
                <Button onClick={handleAddEmployee} disabled={saving} data-testid="save-employee-btn"
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Employee
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ======= EDIT EMPLOYEE MODAL ======= */}
        {showEditModal && editingEmp && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-employee-modal">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">
                  Edit Employee — <span className="text-slate-500 font-mono text-sm">{editingEmp.employment_details?.employee_code}</span>
                </h2>
                <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600" data-testid="close-edit-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {/* Profile Photo Management */}
                <div className="flex items-center gap-4 bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                  <Avatar className="h-16 w-16 border-2 border-white shadow flex items-center justify-center overflow-hidden">
                    <AuthenticatedImage 
                      src={editForm.photoUrl || editingEmp?.personal_details?.photoUrl || editingEmp?.personal_details?.photo_url} 
                      alt="Profile Edit Preview" 
                      className="h-full w-full object-cover rounded-full" 
                      fallbackText={((editForm.first_name?.[0] || '') + (editForm.last_name?.[0] || '')).toUpperCase() || 'EMP'}
                    />
                  </Avatar>
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Profile Photograph</h4>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        id="hr-emp-photo-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error("File is too large (max 2 MB)");
                            return;
                          }
                          if (!editingEmp?.id) {
                            toast.error("Employee must be saved before uploading a photo.");
                            return;
                          }
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const { data } = await axios.post(`${API_URL}/employees/${editingEmp.id}/photo`, formData, {
                              withCredentials: true,
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success("Photo uploaded successfully");
                            setEditForm(prev => ({
                              ...prev,
                              photoUrl: data.photoUrl,
                            }));
                            fetchEmployees();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Upload failed");
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => document.getElementById('hr-emp-photo-upload')?.click()}
                      >
                        Upload / Replace
                      </Button>
                      {(editForm.photoUrl || editingEmp?.personal_details?.photoUrl || editingEmp?.personal_details?.photo_url) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={async () => {
                            if (!window.confirm("Are you sure you want to remove this photo?")) return;
                            if (!editingEmp?.id) {
                              toast.error("Employee must be saved before uploading a photo.");
                              return;
                            }
                            try {
                              await axios.delete(`${API_URL}/employees/${editingEmp.id}/photo`, { withCredentials: true });
                              toast.success("Photo removed successfully");
                              setEditForm(prev => ({
                                ...prev,
                                photoUrl: null,
                              }));
                              fetchEmployees();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to remove photo");
                            }
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">Supports JPG, JPEG, PNG, WEBP. Max 2 MB. Min 200 × 200.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">First Name *</label>
                    <input data-testid="edit-first-name" value={editForm.first_name} onChange={e => setEditForm({...editForm, first_name: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Last Name *</label>
                    <input data-testid="edit-last-name" value={editForm.last_name} onChange={e => setEditForm({...editForm, last_name: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Date of Birth</label>
                    <input data-testid="edit-dob" type="date" value={editForm.date_of_birth} onChange={e => setEditForm({...editForm, date_of_birth: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Gender</label>
                    <select data-testid="edit-gender" value={editForm.gender} onChange={e => setEditForm({...editForm, gender: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Mobile</label>
                    <input data-testid="edit-mobile" value={editForm.mobile} onChange={e => setEditForm({...editForm, mobile: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Work Email *</label>
                    <input data-testid="edit-work-email" type="email" value={editForm.work_email} onChange={e => setEditForm({...editForm, work_email: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Date of Joining</label>
                    <input data-testid="edit-doj" type="date" value={editForm.date_of_joining} onChange={e => setEditForm({...editForm, date_of_joining: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
                    <select data-testid="edit-company" value={editForm.company_id} onChange={e => setEditForm({...editForm, company_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Designation</label>
                    <select data-testid="edit-designation" value={editForm.designation_id} onChange={e => setEditForm({...editForm, designation_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">Select...</option>
                      {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                    <select data-testid="edit-category" value={editForm.employee_category_id} onChange={e => setEditForm({...editForm, employee_category_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">Select...</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Department</label>
                    <select
                      data-testid="edit-department-select"
                      value={editForm.department_id || ''}
                      onChange={e => {
                        const id = e.target.value;
                        const d = departments.find(x => x.id === id);
                        setEditForm({
                          ...editForm,
                          department_id: id,
                          // Keep legacy `department` text in sync for fallback display.
                          department: d ? d.name : '',
                        });
                      }}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">— Select —</option>
                      {departments
                        .filter(d => !editForm.company_id || !d.company_id || d.company_id === editForm.company_id)
                        .map(d => (
                          <option key={d.id} value={d.id}>{`${d.name}${d.code ? ` (${d.code})` : ''}`}</option>
                        ))}
                    </select>
                    {!editForm.department_id && editForm.department && (
                      <p className="text-[10px] text-amber-600 mt-1">
                        Legacy text-only value: <strong>{editForm.department}</strong>. Pick from the master to enable
                        department-wise approval workflows.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Reporting Manager</label>
                    <EmployeeAutocomplete
                      data-testid="edit-reporting-manager"
                      value={editForm.reporting_manager_id || ''}
                      onChange={(id, m) => {
                        setEditForm({
                          ...editForm,
                          reporting_manager_id: id,
                          reporting_manager_name: m
                            ? (m.fullName || m.name || `${m.personal_details?.first_name || ''} ${m.personal_details?.last_name || ''}`).trim()
                            : '',
                        });
                      }}
                      placeholder="Search manager by name or code..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Employment Status</label>
                    <select data-testid="edit-status" value={editForm.employment_status} onChange={e => setEditForm({...editForm, employment_status: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="active">Active</option>
                      <option value="resigned">Resigned</option>
                      <option value="terminated">Terminated</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                {/* O&M / Per-Day-Wage fields — for Labour / Contract employees */}
                <div className="mt-4 pt-4 border-t border-amber-100 bg-amber-50/50 -mx-5 px-5 py-3">
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">
                    🪙 O&M / Per-Day-Wage Fields (only for Labour / Contract groups)
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Skill Category</label>
                      <input data-testid="edit-skill-category"
                        value={editForm.skill_category || ''}
                        onChange={e => setEditForm({...editForm, skill_category: e.target.value})}
                        list="employee-skill-list"
                        placeholder="Highly Skilled / Skilled / Semi Skilled / Unskilled"
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                      <datalist id="employee-skill-list">
                        <option value="Highly Skilled" />
                        <option value="Skilled" />
                        <option value="Semi Skilled" />
                        <option value="Unskilled" />
                        <option value="Apprentice" />
                        <option value="Trainee" />
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Daily Rate Override (₹)</label>
                      <input type="number" min="0" step="0.01"
                        data-testid="edit-daily-rate-override"
                        value={editForm.daily_wage_rate_override || ''}
                        onChange={e => setEditForm({...editForm, daily_wage_rate_override: e.target.value})}
                        placeholder="leave blank → use Wage Master"
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
                      <input data-testid="edit-location"
                        value={editForm.location || ''}
                        onChange={e => setEditForm({...editForm, location: e.target.value})}
                        placeholder="e.g. Chennai / Bangalore"
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-700 mt-2">
                    💡 Skill+Location resolves the ₹/day from <b>Wage Master → Wage Rates</b>.
                    A daily-rate override here takes <b>absolute precedence</b> for this employee only.
                  </p>
                </div>

                {/* ===== KYC + STATUTORY CODES ===== */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="edit-kyc-section">
                  <h4 className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1.5">
                    📋 KYC &amp; Statutory Codes
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">PAN Number</label>
                      <input data-testid="edit-pan-number"
                        value={editForm.pan_number || ''}
                        onChange={e => setEditForm({...editForm, pan_number: e.target.value.toUpperCase()})}
                        placeholder="e.g. ABCDE1234F" maxLength={10}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono uppercase" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Aadhaar Number</label>
                      <input data-testid="edit-aadhaar-number"
                        value={editForm.aadhaar_number || ''}
                        onChange={e => setEditForm({...editForm, aadhaar_number: e.target.value})}
                        placeholder="12-digit number" maxLength={12}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">UAN Number (Employee's PF UAN)</label>
                      <input data-testid="edit-uan-number"
                        value={editForm.uan_number || ''}
                        onChange={e => setEditForm({...editForm, uan_number: e.target.value})}
                        placeholder="12-digit Universal Account Number" maxLength={12}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">ESI Insurance Number</label>
                      <input data-testid="edit-esi-number"
                        value={editForm.esi_number || ''}
                        onChange={e => setEditForm({...editForm, esi_number: e.target.value})}
                        placeholder="10-digit ESI number" maxLength={10}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                    </div>
                  </div>
                  <div className="border-t border-blue-200 pt-3">
                    <p className="text-[11px] text-blue-800 mb-2">
                      <b>📌 Establishment codes</b> — drive the monthly PF/ESI challan grouping.
                      Set these to roll up each employee under the correct EPFO/ESIC sub-code.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-1">
                          PF Establishment Code
                        </label>
                        <input data-testid="edit-pf-code"
                          value={editForm.pf_code || ''}
                          onChange={e => setEditForm({...editForm, pf_code: e.target.value})}
                          placeholder="e.g. TN/MAS/12345/000"
                          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-1">
                          ESI Sub-Code
                        </label>
                        <input data-testid="edit-esi-code"
                          value={editForm.esi_code || ''}
                          onChange={e => setEditForm({...editForm, esi_code: e.target.value})}
                          placeholder="e.g. 33-00-123456-000-0001"
                          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
                <Button variant="outline" onClick={() => setShowEditModal(false)} data-testid="cancel-edit-employee">Cancel</Button>
                <Button onClick={handleEditEmployee} disabled={saving} data-testid="save-edit-employee-btn"
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ======= BULK EDIT MODAL ======= */}
        {showBulkEditModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-edit-modal">
            <div className="bg-white rounded-lg w-full max-w-lg">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Bulk Edit Employees</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Apply changes to <strong>{selectedIds.length}</strong> selected employee{selectedIds.length > 1 ? 's' : ''}</p>
                </div>
                <button onClick={() => setShowBulkEditModal(false)} className="text-slate-400 hover:text-slate-600" data-testid="close-bulk-edit-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Leave a field blank to keep existing values. Only filled fields will be updated.
                </p>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
                  <select data-testid="bulk-edit-company" value={bulkEditForm.company_id} onChange={e => setBulkEditForm({...bulkEditForm, company_id: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    <option value="">— Keep existing —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Designation</label>
                    <select data-testid="bulk-edit-designation" value={bulkEditForm.designation_id} onChange={e => setBulkEditForm({...bulkEditForm, designation_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">— Keep existing —</option>
                      {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                    <select data-testid="bulk-edit-category" value={bulkEditForm.employee_category_id} onChange={e => setBulkEditForm({...bulkEditForm, employee_category_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">— Keep existing —</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Employment Type</label>
                    <select data-testid="bulk-edit-type" value={bulkEditForm.employment_type_id} onChange={e => setBulkEditForm({...bulkEditForm, employment_type_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">— Keep existing —</option>
                      {empTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Employment Status</label>
                    <select data-testid="bulk-edit-status" value={bulkEditForm.employment_status} onChange={e => setBulkEditForm({...bulkEditForm, employment_status: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">— Keep existing —</option>
                      <option value="active">Active</option>
                      <option value="resigned">Resigned</option>
                      <option value="terminated">Terminated</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Department</label>
                  <input data-testid="bulk-edit-department" value={bulkEditForm.department} onChange={e => setBulkEditForm({...bulkEditForm, department: e.target.value})}
                    placeholder="— Keep existing —"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
                <Button variant="outline" onClick={() => setShowBulkEditModal(false)}>Cancel</Button>
                <Button onClick={handleBulkEdit} disabled={bulkEditing} data-testid="bulk-edit-apply-btn"
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {bulkEditing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Apply to {selectedIds.length}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ======= BULK UPLOAD MODAL ======= */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-upload-modal">
            <div className="bg-white rounded-lg w-full max-w-lg">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Bulk Upload Employees</h2>
                <button onClick={() => { setShowBulkModal(false); setBulkResult(null); setBulkFile(null); }}
                  className="text-slate-400 hover:text-slate-600" data-testid="close-bulk-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Upload a CSV of employees. <strong>employee_code</strong> column is optional — leave blank to auto-generate, or supply your existing HR codes. <strong>designation, category, employment_type</strong> accept names (e.g. "Software Engineer") — no IDs needed.
                  </p>
                  <button onClick={handleDownloadTemplate} data-testid="download-bulk-template"
                    className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1 shrink-0 ml-3 whitespace-nowrap">
                    <Download className="w-3 h-3" /> Download Template
                  </button>
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input ref={fileRef} type="file" accept=".csv" onChange={e => setBulkFile(e.target.files[0])}
                    className="hidden" data-testid="bulk-file-input" />
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-2">{bulkFile ? bulkFile.name : 'Click to select CSV file'}</p>
                  <button onClick={() => fileRef.current?.click()} data-testid="select-bulk-file"
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium">
                    Select File
                  </button>
                </div>

                {/* Duplicate-control mode */}
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-700 mb-2">When a row already exists (same Employee Code or Aadhaar)</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: 'reject', label: 'Reject duplicates', hint: 'Safest. Abort upload if any row clashes with existing data.' },
                      { v: 'update', label: 'Update existing',   hint: 'Overwrite the matching record in place.' },
                      { v: 'skip',   label: 'Skip duplicates',   hint: 'Ignore the new row, leave existing as-is.' },
                    ].map((opt) => (
                      <label key={opt.v} className={`cursor-pointer border rounded-md p-2 text-xs ${duplicateMode === opt.v ? 'border-[#0052FF] bg-blue-50 ring-1 ring-[#0052FF]/30' : 'border-slate-300 bg-white hover:border-slate-400'}`}>
                        <input
                          type="radio"
                          name="dup-mode"
                          value={opt.v}
                          checked={duplicateMode === opt.v}
                          onChange={() => setDuplicateMode(opt.v)}
                          data-testid={`dup-mode-${opt.v}`}
                          className="hidden"
                        />
                        <p className="font-medium text-slate-900">{opt.label}</p>
                        <p className="text-slate-500 mt-0.5 leading-snug">{opt.hint}</p>
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    Note: if the SAME CSV file contains the same Employee Code / Aadhaar twice, the entire upload is rejected — you'll see the row numbers below.
                  </p>
                </div>

                {bulkResult && (
                  <div className={`p-3 rounded-md text-sm ${
                    bulkResult.rejected_whole_file ? 'bg-red-50 text-red-800' :
                    bulkResult.error_count > 0 ? 'bg-amber-50 text-amber-800' :
                    'bg-green-50 text-green-800'
                  }`} data-testid="bulk-upload-result">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        {bulkResult.rejected_whole_file ? (
                          <p><strong>❌ File rejected — nothing was saved.</strong></p>
                        ) : bulkResult.error_count > 0 ? (
                          <p>
                            <strong>❌ {bulkResult.error_count} failed</strong>
                            {' · '}<strong>✅ {(bulkResult.created_count || 0) + (bulkResult.updated_count || 0) + (bulkResult.skipped_count || 0)} succeeded</strong>
                            <span className="block text-xs opacity-80 mt-0.5">
                              <strong>{bulkResult.created_count || 0}</strong> created
                              {' · '}<strong>{bulkResult.updated_count || 0}</strong> updated
                              {' · '}<strong>{bulkResult.skipped_count || 0}</strong> skipped
                            </span>
                          </p>
                        ) : (
                          <p>
                            <strong>✅ Success — {bulkResult.created_count || 0}</strong> created
                            {' · '}<strong>{bulkResult.updated_count || 0}</strong> updated
                            {' · '}<strong>{bulkResult.skipped_count || 0}</strong> skipped
                          </p>
                        )}
                      </div>
                      {/* Stage 73.6/.7 — Download Result Report.
                          Builds a CSV from the backend's row_results trail so
                          the admin can verify EVERY row's outcome + the
                          auto-generated employee_code. Includes errors and
                          skipped rows so nothing is hidden. */}
                      {bulkResult.row_results?.length > 0 && (
                        <button
                          data-testid="bulk-download-result-btn"
                          onClick={() => {
                            const niceStatus = (s) => {
                              if (s === 'created') return '✅ Success (created)';
                              if (s === 'updated') return '✅ Success (updated)';
                              if (s === 'skipped') return '⏭ Skipped (duplicate)';
                              if (s === 'would_create') return '🔍 Preview — would create';
                              if (s === 'would_update') return '🔍 Preview — would update';
                              if (s === 'would_skip')   return '🔍 Preview — would skip';
                              if (s === 'error')        return '❌ Error';
                              return s || '';
                            };
                            const rows = bulkResult.row_results.map(r => ({
                              Remark: niceStatus(r.status),
                              CSV_Row: r.row,
                              Employee_Code: r.employee_code || '',
                              Employee_Id: r.employee_id || '',
                              First_Name: r.first_name || '',
                              Last_Name: r.last_name || '',
                              Aadhaar: r.aadhaar_number || '',
                              Specific_Issue: r.reason || '',
                            }));
                            downloadCSV(rows, `bulk_upload_result_${new Date().toISOString().slice(0,10)}.csv`);
                            toast.success(`Downloaded ${rows.length}-row result report`);
                          }}
                          className="text-xs px-2 py-1 bg-white border border-current rounded font-medium hover:bg-slate-50 shrink-0">
                          📥 Download Result Report ({bulkResult.row_results.length} rows)
                        </button>
                      )}
                    </div>
                    {bulkResult.created_codes?.length > 0 && (
                      <details className="mt-1.5">
                        <summary className="text-xs cursor-pointer opacity-80 hover:opacity-100">
                          Codes assigned ({bulkResult.created_codes.length})
                        </summary>
                        <p className="text-[11px] mt-1 font-mono opacity-90 max-h-24 overflow-y-auto">
                          {bulkResult.created_codes.join(', ')}
                        </p>
                      </details>
                    )}
                    {bulkResult.errors?.slice(0, 8).map((e, i) => (
                      <p key={i} className="text-xs mt-1">Row {e.row}: {e.error}</p>
                    ))}
                    {bulkResult.errors?.length > 8 && (
                      <p className="text-xs mt-1 opacity-70">+ {bulkResult.errors.length - 8} more errors — see the downloadable report above</p>
                    )}
                  </div>
                )}

                {/* Stage 73.5 — Smart Retry Banner.
                    When the entire file is rejected because rows already exist,
                    surface a callout with one-click retry buttons. This avoids
                    sending the admin back to edit the CSV when the right answer
                    is simply "Switch to Update / Skip mode and try again". */}
                {bulkResult?.rejected_whole_file && bulkResult.errors?.some(e => (e.error || '').includes('already exists')) && (
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 space-y-3" data-testid="bulk-retry-banner">
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                        <span className="text-white text-sm font-bold">!</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-blue-900">
                          {bulkResult.errors.length} row{bulkResult.errors.length === 1 ? '' : 's'} already exist in this tenant
                        </p>
                        <p className="text-xs text-blue-800 mt-0.5">
                          Nothing was saved — your data is safe. The current mode is <b>Reject duplicates</b>, which is why the upload was halted.
                        </p>
                        <p className="text-xs text-blue-800 mt-1">
                          Pick how you want to handle the existing rows, then retry:
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <button
                        data-testid="bulk-retry-update-btn"
                        disabled={uploading}
                        onClick={() => { setDuplicateMode('update'); handleBulkUpload('update'); }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md p-3 text-left disabled:opacity-50">
                        <p className="text-sm font-bold flex items-center gap-1.5">
                          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>🔁</span>} Retry as Update existing
                        </p>
                        <p className="text-[11px] opacity-90 mt-0.5">Overwrite the matching records with values from this file.</p>
                      </button>
                      <button
                        data-testid="bulk-retry-skip-btn"
                        disabled={uploading}
                        onClick={() => { setDuplicateMode('skip'); handleBulkUpload('skip'); }}
                        className="bg-slate-600 hover:bg-slate-700 text-white rounded-md p-3 text-left disabled:opacity-50">
                        <p className="text-sm font-bold flex items-center gap-1.5">
                          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>⏭</span>} Retry as Skip duplicates
                        </p>
                        <p className="text-[11px] opacity-90 mt-0.5">Leave the matching records as-is; only add net-new rows.</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
                <Button variant="outline" onClick={() => { setShowBulkModal(false); setBulkResult(null); setBulkFile(null); }}>Cancel</Button>
                <Button onClick={handleBulkUpload} disabled={uploading || !bulkFile} data-testid="upload-bulk-btn"
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading… (large files take 30-60s, please wait)' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ============== BULK DELETE MODAL ============== */}
        {showBulkDelete && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !bulkDeleting && setShowBulkDelete(false)}>
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-delete-modal">
              <div className="p-6 border-b border-slate-200 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Bulk Delete Employees</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Tenant-scoped. Admin accounts (HR / Tenant) are always preserved.</p>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">What to delete</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { v: 'company',  label: 'All employees in a company',                hint: 'Wipe every employee under the chosen company.' },
                      { v: 'codes',    label: 'Specific employee codes',                   hint: 'Paste/type a list of codes (comma- or newline-separated).' },
                      { v: 'selected', label: `Currently selected on table (${selectedIds.length})`, hint: 'Use the row checkboxes on the Employees list, then come here.' },
                    ].map((opt) => (
                      <label key={opt.v} className={`cursor-pointer border rounded-lg p-3 ${bulkDeleteForm.target === opt.v ? 'border-red-400 bg-red-50' : 'border-slate-300 hover:border-slate-400'}`}>
                        <div className="flex items-start gap-2">
                          <input
                            type="radio" name="bdtarget" value={opt.v}
                            checked={bulkDeleteForm.target === opt.v}
                            onChange={() => setBulkDeleteForm({ ...bulkDeleteForm, target: opt.v })}
                            data-testid={`bd-target-${opt.v}`}
                            className="mt-0.5"
                          />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{opt.label}</p>
                            <p className="text-xs text-slate-500">{opt.hint}</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {bulkDeleteForm.target === 'company' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Company</label>
                    <select
                      value={bulkDeleteForm.company_id}
                      onChange={(e) => setBulkDeleteForm({ ...bulkDeleteForm, company_id: e.target.value })}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                      data-testid="bd-company-select"
                    >
                      <option value="">Select a company...</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}

                {bulkDeleteForm.target === 'codes' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Employee Codes</label>
                    <textarea
                      value={bulkDeleteForm.codes_text}
                      onChange={(e) => setBulkDeleteForm({ ...bulkDeleteForm, codes_text: e.target.value })}
                      data-testid="bd-codes-textarea"
                      rows={5}
                      placeholder="HECS001, HECS002, HECS003 ..."
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
                    />
                  </div>
                )}

                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3">
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Options</p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkDeleteForm.include_dependents}
                      onChange={(e) => setBulkDeleteForm({ ...bulkDeleteForm, include_dependents: e.target.checked })}
                      data-testid="bd-include-dependents"
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-900">Also wipe dependent data</p>
                      <p className="text-xs text-slate-500">Punches, leave applications, payslips, payroll details, regularizations, biometric mappings, statutory eligibility, etc. Recommended for a fresh re-upload.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkDeleteForm.hard}
                      onChange={(e) => setBulkDeleteForm({ ...bulkDeleteForm, hard: e.target.checked })}
                      data-testid="bd-hard"
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-red-700">Hard delete (permanent)</p>
                      <p className="text-xs text-slate-500">If unchecked, employees are <em>deactivated</em> instead — they disappear from the list but rows stay in the DB and can be reactivated. Hard delete physically removes everything and is unrecoverable.</p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-red-700 mb-1.5 uppercase tracking-wider">Type DELETE to confirm</label>
                  <input
                    value={bulkDeleteForm.confirm_text}
                    onChange={(e) => setBulkDeleteForm({ ...bulkDeleteForm, confirm_text: e.target.value })}
                    data-testid="bd-confirm-input"
                    placeholder="DELETE"
                    className="w-full border-2 border-red-300 rounded-md px-3 py-2 text-sm font-mono uppercase"
                  />
                </div>

                {bulkDeleteResult && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-900" data-testid="bd-result">
                    <p className="font-semibold">
                      {bulkDeleteResult.mode === 'hard' ? 'Permanently deleted' : 'Deactivated'}: <strong>{bulkDeleteResult.deleted_employees}</strong> employee(s)
                      {bulkDeleteResult.deleted_users > 0 && <>, <strong>{bulkDeleteResult.deleted_users}</strong> user logins</>}
                    </p>
                    {bulkDeleteResult.preserved_admins > 0 && (
                      <p className="text-xs mt-1">{bulkDeleteResult.preserved_admins} admin account(s) preserved.</p>
                    )}
                    {Object.keys(bulkDeleteResult.deleted_dependents || {}).length > 0 && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs">Dependent rows deleted</summary>
                        <ul className="text-xs mt-1 space-y-0.5">
                          {Object.entries(bulkDeleteResult.deleted_dependents).map(([k, v]) => (
                            <li key={k}><span className="font-mono">{k}</span>: {v}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
                <Button variant="outline" disabled={bulkDeleting} onClick={() => { setShowBulkDelete(false); setBulkDeleteResult(null); }}>Cancel</Button>
                <Button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting || (bulkDeleteForm.confirm_text || '').toUpperCase() !== 'DELETE'}
                  data-testid="bd-execute-btn"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {bulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  {bulkDeleteForm.hard ? 'Permanently Delete' : 'Deactivate Employees'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ============== FULL TRANSFER MODAL ============== */}
        {transferEmp && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setTransferEmp(null)}>
            <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="transfer-modal">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <ArrowRightLeft className="w-4 h-4 text-purple-600" /> Transfer Employee
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {transferEmp.personal_details?.first_name} {transferEmp.personal_details?.last_name}
                    <span className="font-mono text-slate-400 ml-2">{transferEmp.employment_details?.employee_code}</span>
                  </p>
                </div>
                <button onClick={() => setTransferEmp(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
              </div>

              <div className="p-5 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                  Leave any field blank to keep it unchanged. Filling <strong>CTC group</strong> or <strong>structure code</strong> + <strong>new CTC</strong> creates a fresh CTC assignment effective from the chosen date — payroll will pick it up automatically.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {/* Effective date */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Effective Date *</label>
                    <input type="date" data-testid="transfer-effective-date"
                      value={transferForm.effective_date || ''}
                      onChange={e => setTransferForm({ ...transferForm, effective_date: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                  </div>

                  {/* CTC Group */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">CTC Group</label>
                    <select data-testid="transfer-ctc-group"
                      value={transferForm.new_ctc_group_id || ''}
                      onChange={e => setTransferForm({ ...transferForm, new_ctc_group_id: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm">
                      <option value="">— keep current —</option>
                      {ctcGroups.map(g => <option key={g.id} value={g.id}>{`${g.code} · ${g.name}`}</option>)}
                    </select>
                  </div>

                  {/* Structure */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Salary Structure</label>
                    <select data-testid="transfer-structure"
                      value={transferForm.new_structure_code || ''}
                      onChange={e => setTransferForm({ ...transferForm, new_structure_code: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono">
                      <option value="">— keep current —</option>
                      {structures.map(s => <option key={s.id} value={s.code}>{`${s.code} — ${s.name}`}</option>)}
                    </select>
                  </div>

                  {/* New CTC */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">New CTC (annual ₹)</label>
                    <input type="number" data-testid="transfer-ctc"
                      placeholder="e.g. 1500000"
                      value={transferForm.new_ctc_annual || ''}
                      onChange={e => setTransferForm({ ...transferForm, new_ctc_annual: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono text-right" />
                  </div>

                  {/* Designation */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Designation</label>
                    <input type="text" data-testid="transfer-designation"
                      value={transferForm.new_designation || ''}
                      onChange={e => setTransferForm({ ...transferForm, new_designation: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                  </div>

                  {/* Grade */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Grade</label>
                    <input type="text" data-testid="transfer-grade"
                      value={transferForm.new_grade || ''}
                      onChange={e => setTransferForm({ ...transferForm, new_grade: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                  </div>

                  {/* Department */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Department</label>
                    <input type="text" data-testid="transfer-department"
                      placeholder="department_id"
                      value={transferForm.new_department_id || ''}
                      onChange={e => setTransferForm({ ...transferForm, new_department_id: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
                    <input type="text" data-testid="transfer-location"
                      placeholder="location_id"
                      value={transferForm.new_location_id || ''}
                      onChange={e => setTransferForm({ ...transferForm, new_location_id: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Remarks</label>
                  <textarea rows={2} data-testid="transfer-remarks"
                    placeholder="Reason for transfer (e.g. promotion to Senior Manager + relocation to BLR)"
                    value={transferForm.remarks || ''}
                    onChange={e => setTransferForm({ ...transferForm, remarks: e.target.value })}
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                </div>

                {/* Live statutory eligibility preview */}
                {transferPreview && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="transfer-preview">
                    <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Statutory Impact Preview</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-[11px] text-slate-500">New Monthly CTC</p>
                        <p className="font-semibold text-slate-900 font-mono">₹{transferPreview.monthly_ctc.toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-500">PF (≤ ₹15k)</p>
                        <p className={`font-semibold ${transferPreview.pf_applicable ? 'text-emerald-700' : 'text-rose-600'}`}>
                          {transferPreview.pf_applicable ? '✓ Applicable' : '✗ Not applicable'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-500">ESI (≤ ₹21k)</p>
                        <p className={`font-semibold ${transferPreview.esi_applicable ? 'text-emerald-700' : 'text-rose-600'}`}>
                          {transferPreview.esi_applicable ? '✓ Applicable' : '✗ Not applicable'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-500">PT</p>
                        <p className={`font-semibold ${transferPreview.pt_applicable ? 'text-emerald-700' : 'text-rose-600'}`}>
                          {transferPreview.pt_applicable ? '✓ Applicable (slab)' : '✗ Not applicable'}
                        </p>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                      Eligibility is recomputed using the new monthly gross. Backend persists this on save and the next payroll run honours it.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
                <button onClick={() => setTransferEmp(null)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button data-testid="transfer-submit-btn" onClick={submitTransfer} disabled={transferring}
                  className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                  {transferring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
                  {transferring ? 'Transferring…' : 'Confirm Transfer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ESS LOGIN — single credentials popup (one-time display) */}
        {loginPopup && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="ess-login-popup">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
              <div className="p-6 text-center border-b bg-emerald-50 rounded-t-xl">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                <h2 className="text-xl font-bold text-slate-900">
                  {loginPopup.mode === 'create' ? 'ESS Login Created' : 'Password Reset'}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Share these credentials with {loginPopup.name}. They will not be shown again.
                </p>
              </div>
              <div className="p-6 space-y-3">
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between bg-white rounded px-3 py-2 border border-blue-100">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Login Email</p>
                      <p className="text-sm font-mono font-medium text-slate-900">{loginPopup.email}</p>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(loginPopup.email); toast.success('Copied'); }} className="text-blue-500 hover:text-blue-700 p-1"><Copy className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded px-3 py-2 border border-blue-100">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Temp Password</p>
                      <p className="text-sm font-mono font-medium text-slate-900">{loginPopup.password}</p>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(loginPopup.password); toast.success('Copied'); }} className="text-blue-500 hover:text-blue-700 p-1"><Copy className="w-4 h-4" /></button>
                  </div>
                  <button data-testid="ess-copy-all"
                    onClick={() => { navigator.clipboard.writeText(`Email: ${loginPopup.email}\nPassword: ${loginPopup.password}`); toast.success('Copied'); }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">
                    <Copy className="w-3.5 h-3.5" /> Copy Both
                  </button>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Force-password-change is enabled — the user will be asked to set a new password on first login.
                </p>
              </div>
              <div className="p-4 border-t bg-slate-50 rounded-b-xl text-center">
                <Button data-testid="ess-popup-close" onClick={() => setLoginPopup(null)} className="px-8">Done</Button>
              </div>
            </div>
          </div>
        )}

        {/* ESS LOGIN — bulk results modal with CSV download */}
        {bulkLoginResult && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="ess-bulk-modal">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><KeyRound className="w-5 h-5 text-emerald-600" /> Bulk ESS Login Results</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{bulkLoginResult.total} employee(s) processed</p>
                </div>
                <button onClick={() => setBulkLoginResult(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-5 gap-2 text-center">
                  {[
                    ['created', bulkLoginResult.summary?.created || 0, 'bg-emerald-50 text-emerald-700 border-emerald-200'],
                    ['linked', bulkLoginResult.summary?.linked || 0, 'bg-blue-50 text-blue-700 border-blue-200'],
                    ['exists', bulkLoginResult.summary?.exists || 0, 'bg-slate-50 text-slate-700 border-slate-200'],
                    ['no email', bulkLoginResult.summary?.skipped_no_email || 0, 'bg-amber-50 text-amber-700 border-amber-200'],
                    ['error', bulkLoginResult.summary?.error || 0, 'bg-red-50 text-red-700 border-red-200'],
                  ].map(([label, val, cls]) => (
                    <div key={label} className={`border rounded-lg p-2 ${cls}`}>
                      <p className="text-[10px] uppercase font-semibold tracking-wider">{label}</p>
                      <p className="text-xl font-bold font-mono">{val}</p>
                    </div>
                  ))}
                </div>

                {(bulkLoginResult.results || []).some((r) => r.password) && (
                  <Button data-testid="bulk-csv-download"
                    onClick={() => {
                      const rows = ['name,email,password,status'];
                      (bulkLoginResult.results || []).forEach((r) => {
                        const esc = (s) => `"${(s || '').toString().replace(/"/g, '""')}"`;
                        rows.push([esc(r.name), esc(r.email), esc(r.password || ''), esc(r.status)].join(','));
                      });
                      const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `ess-logins-${Date.now()}.csv`; a.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Download className="w-4 h-4 mr-2" /> Download Credentials CSV (one-time)
                  </Button>
                )}

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase">Name</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase">Email</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase">Status</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase">Password</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bulkLoginResult.results || []).map((r, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2 px-3 text-slate-700">{r.name}</td>
                          <td className="py-2 px-3 font-mono text-slate-700">{r.email || '—'}</td>
                          <td className="py-2 px-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              r.status === 'created' ? 'bg-emerald-100 text-emerald-700' :
                              r.status === 'linked' ? 'bg-blue-100 text-blue-700' :
                              r.status === 'exists' ? 'bg-slate-100 text-slate-700' :
                              r.status === 'skipped_no_email' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{r.status}</span>
                          </td>
                          <td className="py-2 px-3 font-mono text-slate-700">{r.password || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="px-5 py-4 border-t bg-slate-50 flex justify-end sticky bottom-0">
                <Button onClick={() => setBulkLoginResult(null)}>Close</Button>
              </div>
            </div>
          </div>
        )}

        {/* ============== SEND PASSWORD CONFIRMATION MODAL ============== */}
        {showSendPasswordConfirmModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="send-password-confirm-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 text-[#0052FF] flex items-center justify-center font-bold text-lg">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg">Send Login Credentials?</h3>
                  <p className="text-xs text-slate-500">Employee Portal Access</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-6">
                You are about to send login credentials to the registered email address of <strong>{selectedIds.length}</strong> selected employee{selectedIds.length > 1 ? 's' : ''}.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowSendPasswordConfirmModal(false)} disabled={sendingPasswordEmail} data-testid="send-password-cancel">
                  Cancel
                </Button>
                <Button onClick={handleSendPasswordEmail} disabled={sendingPasswordEmail} className="bg-[#0052FF] hover:bg-[#0043D1] text-white" data-testid="send-password-confirm">
                  {sendingPasswordEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send Password'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ============== SEND PASSWORD RESULT MODAL ============== */}
        {sendPasswordResult && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="send-password-result-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 border border-slate-200">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  <h3 className="font-semibold text-slate-900 text-lg">Password Email Dispatch Results</h3>
                </div>
                <button onClick={() => setSendPasswordResult(null)} className="text-slate-400 hover:text-slate-600" data-testid="close-send-password-result">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-900 flex items-center justify-between">
                  <span className="text-sm font-medium">✓ Password emails sent</span>
                  <span className="text-xl font-bold font-mono" data-testid="sent-count">{sendPasswordResult?.summary?.sent || 0}</span>
                </div>
                <div className={`p-3 rounded-lg flex items-center justify-between border ${sendPasswordResult?.summary?.failed > 0 ? 'bg-red-50 border-red-200 text-red-900' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                  <span className="text-sm font-medium">✕ Failed</span>
                  <span className="text-xl font-bold font-mono" data-testid="failed-count">{sendPasswordResult?.summary?.failed || 0}</span>
                </div>
              </div>

              {/* Failures Breakdown */}
              {sendPasswordResult?.results?.some(r => r.status === 'failed') && (
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-semibold uppercase text-slate-500">Failed Employees Breakdown:</p>
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {sendPasswordResult.results.filter(r => r.status === 'failed').map((item, idx) => (
                      <div key={idx} className="p-2.5 text-xs flex items-center justify-between bg-red-50/50">
                        <div>
                          <span className="font-mono font-bold text-slate-800 mr-2">{item.employee_code || 'N/A'}</span>
                          <span className="text-slate-600">{item.email || 'No email configured'}</span>
                        </div>
                        <span className="text-red-600 font-medium bg-red-100 px-2 py-0.5 rounded">{item.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button onClick={() => setSendPasswordResult(null)} className="bg-[#0052FF] hover:bg-[#0043D1] text-white" data-testid="done-send-password-result">
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ============== EMPLOYEE VIEW MODAL ============== */}
        {viewingEmpId && (
          <EmployeeViewModal
            employeeId={viewingEmpId}
            onClose={() => setViewingEmpId(null)}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default Employees;
